import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams } from '@ag-grid-community/core';
import { CommonModule } from '@angular/common';
import { Component, ComponentRef, effect, signal, viewChild, ViewContainerRef } from '@angular/core';

interface TreeRendererParams extends ICellRendererParams {
    depthRendererMappings?: { [level: number]: { renderer: any; rendererParams: Function } };
    defaultRenderer?: any;
    getDepth?: (params: ICellRendererParams) => number;
}

@Component({
    selector: 'app-generic-tree-cell-renderer',
    imports: [CommonModule],
    templateUrl: './generic-tree-cell-renderer.component.html',
    styleUrl: './generic-tree-cell-renderer.component.scss',
})
export class GenericTreeCellRendererComponent implements ICellRendererAngularComp {
    container = viewChild('container', { read: ViewContainerRef });

    params = signal<TreeRendererParams | undefined>(undefined);
    private componentRef: ComponentRef<any> | null = null;

    constructor() {
        effect(() => {
            const params = this.params();
            if (params) {
                this.renderComponent(params);
            }
        });
    }

    agInit(params: TreeRendererParams): void {
        this.params.set(params);
    }

    refresh(params: TreeRendererParams): boolean {
        this.params.set(params);
        return true;
    }

    private renderComponent(params: TreeRendererParams): void {
        if (this.componentRef) {
            this.componentRef.destroy();
            this.componentRef = null;
        }
        this.container()?.clear();

        const rowDepth = this.getDepth(params);
        const depthRendererMappings = params.depthRendererMappings || {};
        const currentDepthRenderer = depthRendererMappings[rowDepth] || params.defaultRenderer;

        if (currentDepthRenderer) {
            this.componentRef = this.container()!.createComponent(currentDepthRenderer.renderer);

            if (this.componentRef.instance.agInit) {
                this.componentRef.instance.agInit({ ...params, ...(currentDepthRenderer?.rendererParams?.(params) ?? {}) });
            }
        } else {
            const textNode = document.createTextNode(params.value || '');
            this.container()!.element.nativeElement.appendChild(textNode);
        }
    }

    private getDepth(params: TreeRendererParams): number {
        if (params.getDepth) {
            return params.getDepth(params);
        }

        if (params.node.level !== undefined) {
            return params.node.level;
        }

        return 0;
    }

    ngOnDestroy(): void {
        if (this.componentRef) {
            this.componentRef.destroy();
        }
    }
}
